import { useEffect, useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router";

const scrollPositions = new Map<string, number>();
const MAX_SAVED_SCROLL_POSITIONS = 100;

const saveScrollPosition = (key: string, position: number) => {
  scrollPositions.delete(key);
  scrollPositions.set(key, position);
  if (scrollPositions.size > MAX_SAVED_SCROLL_POSITIONS) {
    const oldestKey = scrollPositions.keys().next().value;
    if (oldestKey) scrollPositions.delete(oldestKey);
  }
};

/** Restores a previous route only on browser/site back; ordinary navigation starts at the top. */
const RouteScrollRestoration = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    return () => {
      saveScrollPosition(location.key, window.scrollY);
    };
  }, [location.key]);

  useLayoutEffect(() => {
    if (navigationType !== "POP") {
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    const savedPosition = scrollPositions.get(location.key);
    if (savedPosition === undefined) return;

    let frame = 0;
    const restore = () => {
      window.scrollTo({ top: savedPosition, behavior: "auto" });
      // A second pass lets lazy route content establish its height before the
      // browser clamps the restored offset.
      if (frame++ < 1) requestAnimationFrame(restore);
    };
    const frameId = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(frameId);
  }, [location.key, navigationType]);

  return null;
};

export default RouteScrollRestoration;
